import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Icon, ReqRow, SectionLabel, TypeBadge, EmptyState, type IconName } from '@/components';
import { useCourse, useEnrollments } from '@/hooks/queries';
import { useLocalized } from '@/hooks/useLocalized';
import { requirementState, courseGating } from '@/lib/requirements';
import { REQ } from '@/data/dictionaries';
import { formatDuration } from '@/lib/format';

const STRIPE = 'repeating-linear-gradient(135deg, rgba(255,255,255,.16), rgba(255,255,255,.16) 1px, transparent 1px, transparent 12px)';

export function DesktopCourseDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { L } = useLocalized();
  const { data: course } = useCourse(id);
  const { data: enrollments = [] } = useEnrollments();

  if (!course) return <EmptyState icon="book" title="Course not found" />;
  const e = enrollments.find((x) => x.courseId === id);
  const enrolled = !!e;
  const rs = requirementState(course, e);
  const met = course.requirements.filter((k) => rs[k]?.met).length;
  const g = courseGating(course, e);
  const nextIdx = course.lessons.findIndex((l) => !l.done);
  const continueTo = nextIdx >= 0 ? `/learn/learning/${id}/lesson/${nextIdx}` : g.postTestLocked ? `/learn/course/${id}/attendance` : `/learn/course/${id}/test/post`;

  const facts: { ic: IconName; l: string; v: string }[] = [
    { ic: 'clock', l: 'Online lessons', v: formatDuration(course.lessons.reduce((s, l) => s + (l.minutes ?? 0), 0)) },
    { ic: 'award', l: 'Certificate valid', v: course.certYears ? `${course.certYears} yr` : '—' },
    { ic: 'cal', l: 'Enrollment', v: `${course.enrollStart}–${course.enrollEnd}` },
    { ic: 'users', l: 'Seats', v: `${course.capacity - course.enrolled} of ${course.capacity}` },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 32 }}>
      <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm" style={{ marginBottom: 18 }}>
        <Icon name="chevL" size={15} /> Back
      </button>

      <div style={{ borderRadius: 'var(--r-lg)', overflow: 'hidden', border: '1px solid var(--line)', marginBottom: 24 }}>
        {course.coverUrl ? (
          // Show the uploaded cover in full (natural ratio) instead of cropping to a fixed box.
          <div style={{ position: 'relative' }}>
            <img src={course.coverUrl} alt={course.title.en} style={{ display: 'block', width: '100%', height: 'auto' }} />
            <div style={{ position: 'absolute', left: 20, bottom: 20, display: 'flex', gap: 8 }}>
              <TypeBadge type={course.type} />
              <Badge kind="line" style={{ background: 'rgba(251,250,247,.85)' }}>{course.code}</Badge>
            </div>
          </div>
        ) : (
          <div style={{ aspectRatio: '3 / 1', background: course.cover, position: 'relative', display: 'flex', alignItems: 'flex-end', padding: 20 }}>
            <div style={{ position: 'absolute', inset: 0, background: STRIPE }} />
            <div style={{ display: 'flex', gap: 8, position: 'relative' }}>
              <TypeBadge type={course.type} />
              <Badge kind="line" style={{ background: 'rgba(251,250,247,.85)' }}>{course.code}</Badge>
            </div>
          </div>
        )}
        <div style={{ padding: 24, background: 'var(--card)' }}>
          <h1 style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-.02em', margin: 0 }}>{course.title.en}</h1>
          <div className="th" style={{ fontSize: 16, color: 'var(--ink-2)', marginTop: 4 }}>{L(course.title)}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--ink-3)', fontSize: 13, marginTop: 12 }}>
            <Icon name="bldg" size={15} /> Hosted by {course.host}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 24, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.6, color: 'var(--ink-2)' }}>{course.summary}</p>

          {/* Pre-test gate — lessons unlock only after the pre-test is taken */}
          {g.lessonsLocked && (
            <div className="card" style={{ padding: 16, background: 'var(--ochre-soft)', borderColor: 'transparent' }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ color: 'var(--ochre)' }}><Icon name="lock" size={20} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>Take the pre-test first · ทำข้อสอบก่อนเรียน</div>
                  <div style={{ fontSize: 13.5, color: 'var(--ink-2)', marginTop: 2 }}>A short pre-test unlocks the lessons and sets your baseline.</div>
                  <button className="btn btn-sm" style={{ marginTop: 12, background: 'var(--ochre)', color: 'var(--on-accent)' }} onClick={() => navigate(`/learn/course/${id}/test/pre`)}>
                    <Icon name="doc" size={15} /> Start pre-test
                  </button>
                </div>
              </div>
            </div>
          )}

          {course.lessons.length > 0 && (
            <div>
              <SectionLabel>Lessons · บทเรียน</SectionLabel>
              <div className="card divide">
                {course.lessons.map((l, i) => {
                  const locked = !enrolled || g.lessonsLocked || (i > 0 && !course.lessons[i - 1].done);
                  return (
                  <button key={l.id} onClick={() => !locked && navigate(`/learn/learning/${id}/lesson/${i}`)} style={{ all: 'unset', display: 'block', width: '100%', cursor: locked ? 'default' : 'pointer', opacity: locked ? 0.55 : 1 }}>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, flex: 'none', display: 'grid', placeItems: 'center', background: l.done ? 'var(--sage)' : 'var(--paper-2)', color: l.done ? '#fbfaf7' : 'var(--ink-3)' }}>
                          {l.done ? <Icon name="check" size={15} stroke={2.4} /> : <Icon name={l.kind === 'video' ? 'play' : 'file'} size={14} fill={l.kind === 'video'} />}
                        </div>
                        <div>
                          <div style={{ fontSize: 14.5, fontWeight: 500 }}>{l.title.en}</div>
                          <div className="th" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{l.title.th} · {l.minutes} min</div>
                        </div>
                      </div>
                      {locked ? <Icon name="lock" size={15} style={{ color: 'var(--ink-4)' }} /> : <Icon name="chevR" size={16} style={{ color: 'var(--ink-4)' }} />}
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, position: 'sticky', top: 86 }}>
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18 }}>
              {facts.map((f, i) => (
                <div key={i}>
                  <div style={{ display: 'flex', gap: 7, alignItems: 'center', color: 'var(--ink-3)', fontSize: 12, marginBottom: 4 }}><Icon name={f.ic} size={14} /> {f.l}</div>
                  <div style={{ fontSize: 14.5, fontWeight: 500 }}>{f.v}</div>
                </div>
              ))}
            </div>
            {!enrolled ? (
              g.canEnroll ? (
                <button className="btn btn-primary btn-block" onClick={() => navigate(`/learn/catalog/${id}/enroll`)}>
                  Enroll · ลงทะเบียน <Icon name="arrowR" size={16} />
                </button>
              ) : (
                <>
                  <button className="btn btn-block" disabled style={{ background: 'var(--paper-2)', color: 'var(--ink-3)' }}>
                    <Icon name="lock" size={16} /> {g.capacityFull ? 'Class full' : 'Enrollment closed'}
                  </button>
                  <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--ink-3)', textAlign: 'center' }}>
                    {g.capacityFull ? 'All seats are taken.' : 'Not open for enrollment.'}
                  </p>
                </>
              )
            ) : g.lessonsLocked ? (
              <button className="btn btn-primary btn-block" onClick={() => navigate(`/learn/course/${id}/test/pre`)}>
                <Icon name="doc" size={16} /> Start pre-test · ทำข้อสอบก่อนเรียน
              </button>
            ) : (
              <>
                <button className="btn btn-soft btn-block" onClick={() => navigate(continueTo)}>Continue course</button>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <button className="btn btn-ghost btn-sm" style={{ flex: '1 0 45%', color: rs['posttest']?.met ? 'var(--sage-deep)' : undefined }} disabled={g.postTestLocked || rs['posttest']?.met} onClick={() => navigate(`/learn/course/${id}/test/post`)}><Icon name={rs['posttest']?.met ? 'checkCircle' : g.postTestLocked ? 'lock' : 'doc'} size={15} /> {rs['posttest']?.met ? 'Post-test done' : 'Post-test'}</button>
                  {rs['posttest']?.met && (
                    <button className="btn btn-ghost btn-sm" style={{ flex: '1 0 45%' }} onClick={() => navigate(`/learn/course/${id}/review`)}><Icon name="search" size={15} /> Review answers</button>
                  )}
                  {course.requirements.includes('attend') && (
                    <button className="btn btn-ghost btn-sm" style={{ flex: '1 0 45%' }} disabled={g.checkinLocked} onClick={() => navigate(`/learn/course/${id}/attendance`)}><Icon name={g.checkinLocked ? 'lock' : 'qr'} size={15} /> Check-in</button>
                  )}
                  {g.evalRequired && (
                    <button className="btn btn-ghost btn-sm" style={{ flex: '1 0 45%', color: g.evalDone ? 'var(--sage-deep)' : undefined }} onClick={() => navigate(`/learn/course/${id}/eval`)}>
                      <Icon name={g.evalDone ? 'checkCircle' : 'star'} size={15} /> {g.evalDone ? 'Eval done' : 'Evaluation'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="card" style={{ padding: '4px 18px' }}>
            <div style={{ padding: '14px 0 6px' }}>
              <SectionLabel action={<span className="u-mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{met}/{course.requirements.length}</span>}>
                Requirements
              </SectionLabel>
            </div>
            {course.requirements.map((k, i) => (
              <div key={k} style={{ borderTop: i ? '1px solid var(--line)' : 'none' }}>
                <ReqRow met={rs[k].met} pending={rs[k].pending} label={REQ[k].en} th={REQ[k].th} detail={rs[k].detail} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
